import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/common/Layout';
import { settingService, CreateSettingData, UpdateSettingData } from '../services/settingService';
import { Setting } from '../services/settingService';
import { useAuth } from '../context/AuthContext';
import { FiX } from 'react-icons/fi';
import Icon from '../components/common/Icon';

const Settings: React.FC = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSetting, setEditingSetting] = useState<Setting | null>(null);
  const [filterType, setFilterType] = useState('customer_status');
  const [formData, setFormData] = useState<CreateSettingData>({
    setting_type: 'customer_status',
    setting_key: '',
    setting_value: '',
    display_name: '',
    color_code: '',
    description: '',
    display_order: 0,
    is_active: true
  });

  const isOrgAdmin = user?.role === 'org_admin';
  const hasLoadedRef = useRef(false);
  const lastFilterTypeRef = useRef<string | null>(null);

  // Load settings on mount and when filterType changes
  useEffect(() => {
    if (!isOrgAdmin) {
      return;
    }

    // Prevent duplicate calls from React StrictMode
    // Only reload if filterType actually changed
    if (hasLoadedRef.current && lastFilterTypeRef.current === filterType) {
      return;
    }

    hasLoadedRef.current = true;
    lastFilterTypeRef.current = filterType;
    loadSettings();
  }, [filterType, isOrgAdmin]);

  const loadSettings = async () => {
    // Prevent duplicate concurrent calls
    if (loading) {
      return;
    }
    try {
      setLoading(true);
      const data = await settingService.getAll({ setting_type: filterType });
      setSettings(data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingSetting) {
        await settingService.update(editingSetting.id, formData);
      } else {
        await settingService.create(formData);
      }
      setShowModal(false);
      setEditingSetting(null);
      setFormData({
        setting_type: filterType,
        setting_key: '',
        setting_value: '',
        display_name: '',
        color_code: '',
        description: '',
        display_order: 0,
        is_active: true
      });
      loadSettings();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save setting');
    }
  };

  const handleEdit = (setting: Setting) => {
    setEditingSetting(setting);
    setFormData({
      setting_type: setting.setting_type,
      setting_key: setting.setting_key,
      setting_value: setting.setting_value,
      display_name: setting.display_name,
      color_code: setting.color_code || '',
      description: setting.description || '',
      display_order: setting.display_order,
      is_active: setting.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to deactivate this setting?')) {
      return;
    }
    try {
      await settingService.delete(id);
      loadSettings();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete setting');
    }
  };

  const getColorClass = (colorCode: string): string => {
    const colorMap: { [key: string]: string } = {
      'RED': 'bg-red-100 text-red-800 border-red-300',
      'WHITE': 'bg-gray-100 text-gray-800 border-gray-300',
      'GREEN': 'bg-green-100 text-green-800 border-green-300',
      'SKY': 'bg-cyan-100 text-cyan-800 border-cyan-300',
      'YELLOW': 'bg-yellow-100 text-yellow-800 border-yellow-300'
    };
    return colorMap[colorCode] || 'bg-gray-100 text-gray-800';
  };

  if (!isOrgAdmin) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
            Access Denied: Only Organization Administrators can manage settings.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-3 sm:space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Settings Management</h1>
          <button
            onClick={() => {
              setEditingSetting(null);
              setFormData({
                setting_type: filterType,
                setting_key: '',
                setting_value: '',
                display_name: '',
                color_code: '',
                description: '',
                display_order: 0,
                is_active: true
              });
              setShowModal(true);
            }}
            className="bg-yellow-500 text-black-950 px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-yellow-600 text-sm sm:text-base font-medium w-full sm:w-auto font-bold"
          >
            Add Setting
          </button>
        </div>

        {/* Filter */}
        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm">
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
            Setting Type
          </label>
          <select
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setFormData({ ...formData, setting_type: e.target.value });
            }}
            className="w-full sm:w-64 border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
          >
            <option value="customer_status">Customer Status</option>
            <option value="company_sales_officer">Company Sales Officer</option>
            <option value="project_state">Project State</option>
            <option value="project_district">Project District</option>
            <option value="call_outcome">Call outcome</option>
            <option value="call_status">Call status</option>
          </select>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-6 text-sm text-gray-600">Loading...</div>
        ) : (
          <div className="bg-white shadow-sm overflow-hidden rounded-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Key
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Display Name
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                      Value
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Color
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      Order
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {settings.map((setting) => (
                    <tr key={setting.id} className="hover:bg-gray-50">
                      <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900">
                        {setting.setting_key}
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                        {setting.display_name}
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap text-xs sm:text-sm text-gray-500 hidden sm:table-cell">
                        {setting.setting_value}
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap">
                        {setting.color_code && (
                          <span className={`px-1.5 sm:px-2 inline-flex text-xs leading-4 sm:leading-5 font-semibold rounded-full border ${getColorClass(setting.color_code)}`}>
                            {setting.color_code}
                          </span>
                        )}
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap text-xs sm:text-sm text-gray-500 hidden md:table-cell">
                        {setting.display_order}
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap">
                        <span className={`px-1.5 sm:px-2 inline-flex text-xs leading-4 sm:leading-5 font-semibold rounded-full ${
                          setting.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {setting.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap text-xs sm:text-sm font-medium">
                        <button
                          onClick={() => handleEdit(setting)}
                          className="text-yellow-600 hover:text-yellow-700 mr-2 sm:mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(setting.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Deactivate
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-900">
                  {editingSetting ? 'Edit Setting' : 'Add Setting'}
                </h3>
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingSetting(null); }}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  aria-label="Close"
                >
                  <Icon component={FiX} className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Setting Type *</label>
                    <input
                      type="text"
                      required
                      value={formData.setting_type}
                      onChange={(e) => setFormData({ ...formData, setting_type: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Setting Key *</label>
                    <input
                      type="text"
                      required
                      value={formData.setting_key}
                      onChange={(e) => setFormData({ ...formData, setting_key: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                      disabled={!!editingSetting}
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Setting Value *</label>
                    <input
                      type="text"
                      required
                      value={formData.setting_value}
                      onChange={(e) => setFormData({ ...formData, setting_value: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Display Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.display_name}
                      onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Color Code</label>
                    <select
                      value={formData.color_code || ''}
                      onChange={(e) => setFormData({ ...formData, color_code: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    >
                      <option value="">Select Color</option>
                      <option value="RED">RED</option>
                      <option value="WHITE">WHITE</option>
                      <option value="GREEN">GREEN</option>
                      <option value="SKY">SKY</option>
                      <option value="YELLOW">YELLOW</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Display Order</label>
                    <input
                      type="number"
                      value={formData.display_order}
                      onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    />
                  </div>
                  <div className="col-span-1 sm:col-span-2">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">Active</span>
                    </label>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-3 sm:pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingSetting(null);
                    }}
                    className="px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1.5 sm:px-4 sm:py-2 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold"
                  >
                    {editingSetting ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Settings;

